/**
 * riders.ts — Centralized rider (sản phẩm bổ trợ) definitions for Xanh Ước Mơ
 *
 * Source of truth for:
 * - Base annual premium (Year 1) per rider
 * - Display metadata (name, color, category, description)
 * - Repricing behavior (age-repriced vs fixed)
 * - Coverage limits
 *
 * All UI screens (M06, M07, M13) and calculation engine MUST import from here.
 * Prices verified against Curex portal screenshots 9_input/ (24/06/2026):
 *   - 2_song_khoe_moi_ngay.png      → R1 = 1.790.000 đ
 *   - 3_la_chan_xanh.png            → R2 = 284.000 đ (gói Đầy đủ mặc định)
 *   - 4_du_phong_xanh.png           → R3 = 1.717.000 đ
 *   - 5_ho_ve_xanh.png              → R4 = 142.000 đ
 *   - 6_bao_thanh_xanh.png          → R5 = 796.000 đ (mode Đính kèm)
 */

export type RiderRepricingMode = 'age-repriced' | 'fixed';

export interface RiderBenefitBullet {
  title: string;
  detail: string;
}

export interface Rider {
  id: string;
  name: string;
  /** Base annual premium at Year 1 (VND) */
  basePrice: number;
  /** Color used in UI badges, icons, chart petals */
  color: string;
  /** Short category label (Sức khỏe, Bệnh lý, ...) */
  category: string;
  /** One-line description shown on rider card */
  shortDesc: string;
  /** Mode for annual repricing */
  repricing: RiderRepricingMode;
  /** Default coverage limit (STBH rider) in VND */
  defaultCoverageLimit: number;
  /** Default age-protection cap (e.g. "75 tuổi") */
  protectionUntilAge?: number;
  /** Detailed benefit bullets shown in petal detail panel */
  bullets: RiderBenefitBullet[];
  /** Whether this rider is auto-included (complimentary) for XUM */
  complimentary?: boolean;
  /** Whether the rider supports "Đính kèm" (paid) and "Tặng kèm" (free) modes */
  hasComplimentaryMode?: boolean;
}

/**
 * Age-repriced riders use the same formula as R1 (verified from Curex data):
 *   Yr 1: base
 *   Yr 2: base + 10,000
 *   Yr 3+: base + 10,000 + (year - 2) × 40,000
 *
 * Fixed riders keep the same premium every year.
 */
export const AGE_REPRICED_RIDER_IDS: Set<string> = new Set(['R1', 'R2', 'R4']);
export const HEALTH_RIDER_ANNUAL_INCREMENT = 40_000;
export const HEALTH_RIDER_YEAR_2_INCREMENT = 10_000;

export const RIDERS: Rider[] = [
  {
    id: 'R1',
    name: 'Sống Khỏe Mỗi Ngày',
    basePrice: 1_790_000,
    color: '#E11D48',
    category: 'Sức khỏe',
    shortDesc: 'Bảo lãnh viện phí, sức khỏe cao cấp',
    repricing: 'age-repriced',
    defaultCoverageLimit: 125_000_000,
    bullets: [
      {
        title: 'Hạn mức bảo vệ y tế',
        detail: 'Lên đến 1 Tỷ VNĐ/năm (gói Bạc: 125 triệu/năm).',
      },
      {
        title: 'Điều trị nội trú & phẫu thuật',
        detail: 'Chi trả 100% chi phí điều trị nội trú, phẫu thuật thực tế tại bệnh viện.',
      },
      {
        title: 'Bảo lãnh viện phí 24/7',
        detail: 'Hệ thống hơn 300 bệnh viện chất lượng cao trong nước.',
      },
    ],
  },
  {
    id: 'R2',
    name: 'Lá Chắn Xanh',
    basePrice: 284_000,
    color: '#D97706',
    category: 'Bệnh lý',
    shortDesc: 'Bảo vệ bệnh lý nghiêm trọng',
    repricing: 'age-repriced',
    defaultCoverageLimit: 100_000_000,
    bullets: [
      {
        title: 'Bảo vệ tài chính trước bệnh lý nghiêm trọng',
        detail: '134 Bệnh lý nghiêm trọng qua nhiều giai đoạn.',
      },
      {
        title: 'Chi trả tối đa 325% STBH',
        detail: 'Hỗ trợ tài chính trước các bệnh lý nghiêm trọng giai đoạn cuối.',
      },
      {
        title: 'Giai đoạn sớm & đặc biệt',
        detail: 'Chi trả 25 triệu đối với giai đoạn sớm và bệnh lý đặc biệt.',
      },
    ],
  },
  {
    id: 'R3',
    name: 'Dự Phòng Xanh',
    basePrice: 1_717_000,
    color: '#00A651',
    category: 'Đóng phí',
    shortDesc: 'Hỗ trợ đóng phí khi gặp rủi ro',
    repricing: 'fixed',
    defaultCoverageLimit: 1_000_000, // per day
    protectionUntilAge: 75,
    bullets: [
      {
        title: 'Miễn đóng phí còn lại',
        detail: 'Miễn đóng toàn bộ phí bảo hiểm còn lại của hợp đồng khi Người mua gặp biến cố sức khỏe lớn.',
      },
      {
        title: 'Duy trì hợp đồng',
        detail: 'Đảm bảo hợp đồng bảo hiểm chính và các quyền lợi bổ trợ khác tiếp tục duy trì hiệu lực.',
      },
      {
        title: 'Trợ cấp nằm viện',
        detail: '1.000.000 đ/ngày nội trú; 3.000.000 đ/ngày ICU.',
      },
    ],
  },
  {
    id: 'R4',
    name: 'Hộ Vệ Xanh',
    basePrice: 142_000,
    color: '#2563EB',
    category: 'Tai nạn',
    shortDesc: 'Bảo vệ tai nạn giao thông & sinh hoạt',
    repricing: 'age-repriced',
    defaultCoverageLimit: 100_000_000,
    protectionUntilAge: 75,
    bullets: [
      {
        title: 'Chi trả 300% STBH',
        detail: 'Đối với thương tật do tai nạn.',
      },
      {
        title: 'Bảo vệ 24/7 toàn cầu',
        detail: 'Tai nạn giao thông, sinh hoạt, thể thao, lao động.',
      },
      {
        title: 'Hỗ trợ chi phí nằm viện',
        detail: 'Chi phí nằm viện nội trú đặc biệt do tai nạn.',
      },
    ],
  },
  {
    id: 'R5',
    name: 'Bảo Thân Xanh',
    basePrice: 0, // Default: complimentary mode
    color: '#7C3AED',
    category: 'Nằm viện',
    shortDesc: 'Hỗ trợ trợ cấp y tế (Tặng kèm)',
    repricing: 'fixed',
    defaultCoverageLimit: 750_000_000,
    protectionUntilAge: 99,
    complimentary: true,
    hasComplimentaryMode: true,
    bullets: [
      {
        title: 'Trợ cấp tiền mặt hàng ngày',
        detail: 'Từ 500.000đ đến 2.000.000đ/ngày nằm viện thực tế.',
      },
      {
        title: 'Nhân đôi trợ cấp ICU',
        detail: 'Khi nằm viện điều trị tại Khoa chăm sóc đặc biệt.',
      },
      {
        title: 'Thủ tục nhanh chóng',
        detail: 'Thanh toán đơn giản, nhanh chóng qua ứng dụng di động.',
      },
    ],
  },
];

/**
 * Lookup helpers
 */
export const getRiderById = (id: string): Rider | undefined =>
  RIDERS.find((r) => r.id === id);

export const getRiderBasePrice = (id: string): number =>
  getRiderById(id)?.basePrice ?? 0;

/**
 * Map of base prices, used by calculation engine.
 * Mirrors the historical RIDERS_BASE_PREMIUM constant.
 */
export const RIDERS_BASE_PREMIUM: Record<string, number> = RIDERS.reduce(
  (acc, r) => {
    acc[r.id] = r.basePrice;
    return acc;
  },
  {} as Record<string, number>,
);

/**
 * Backward-compatible alias (kept for calculations.ts imports).
 */
export const RIDERS_PREMIUM = RIDERS_BASE_PREMIUM;

/**
 * R5 (Bảo Thân Xanh) coverage limit by mode.
 * Mode "Tặng kèm" (complimentary) = 750M; mode "Đính kèm" (paid) = 950M.
 * The "Đính kèm" premium is 796,000 đ/năm.
 */
export const R5_MODES = {
  complimentary: {
    label: 'Tặng kèm',
    coverageLimit: 750_000_000,
    premium: 0,
  },
  attached: {
    label: 'Đính kèm',
    coverageLimit: 950_000_000,
    premium: 796_000,
  },
} as const;